import React from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { motion } from "framer-motion";

// style rules
import { Animation, Typography, Colors, Sizes } from "../../styles";
import MoreIcon from "./MoreIcon";

const StaffCard = ({ toggleStaffModal, staffMember }) => {
	return (
		<CardContainer
			key={staffMember.id}
			initial={"initial"}
			animate={"animate"}
			exit={"initial"}
			variants={Animation.cards.opacityVariants}
			isClickable={staffMember.bio}
			onClick={staffMember.bio && (() => toggleStaffModal(staffMember))}
			hasButton={!!staffMember.bio}
			onKeyDown={
				staffMember.bio &&
				((e) => {
					if (e.key === "Enter") {
						toggleStaffModal(staffMember);
					}
				})
			}
		>
			<div>
				<CardHeader>
					{staffMember.photo ? (
						<img
							src={staffMember.photo}
							alt={`Image for ${staffMember.name}`}
						/>
					) : (
						<img
							src={
								mainScript.assetPath +
								"images/png/graphics/people-placeholder.png"
							}
							alt={"Staff member card placeholder"}
						/>
					)}
				</CardHeader>
				<CardContentContainer>
					<CardContent>
						<CardContentTop>
							{staffMember.bio ? (
								<CardTitleLink
									className="esc-link-styling"
									href={staffMember.url}
									dangerouslySetInnerHTML={{ __html: staffMember.name }}
								></CardTitleLink>
							) : (
								<CardTitle
									dangerouslySetInnerHTML={{ __html: staffMember.name }}
								></CardTitle>
							)}
							<CardSubTitle
								dangerouslySetInnerHTML={{ __html: staffMember.job_title }}
							></CardSubTitle>
						</CardContentTop>
						<CardContentBottom>
							<CardMeta
								dangerouslySetInnerHTML={{
									__html: staffMember.professional_title,
								}}
							></CardMeta>
							<CardMeta
								dangerouslySetInnerHTML={{ __html: staffMember.location }}
							></CardMeta>
						</CardContentBottom>
					</CardContent>
				</CardContentContainer>
			</div>
			{staffMember.bio && (
				<Button
					type="button"
					aria-label={`Find out more about ${staffMember.title}`}
					className="esc-button-hover"
				>
					<MoreIcon />
				</Button>
			)}
		</CardContainer>
	);
};

export default StaffCard;

StaffCard.propTypes = {
	staffMember: PropTypes.object,
	toggleStaffModal: PropTypes.func,
};
const CardContainer = styled(motion.li)`
	display: flex;
	flex-direction: column;
	gap: 2rem;
	background-color: ${Colors.fp.pineGreen40};
	border-radius: 3rem;
	position: relative;
	margin-bottom: unset;
	justify-content: space-between;
	cursor: pointer;
	padding-bottom: ${(props) => (props.hasButton ? "0" : "3rem")};

	&:hover {
		@media only screen and (min-width: ${Sizes.screens.large}) {
			img {
				transform: ${(props) => props.isClickable && "scale(1.05)"};
			}

			button {
				svg {
					transform: scale(1.1) rotate(180deg);
				}
			}
		}
	}

	&:focus-within {
		img {
			transform: scale(1.05);
		}

		button {
			svg {
				transform: scale(1.4) rotate(-22.5deg);
			}
		}
	}
`;

const CardHeader = styled.div`
	aspect-ratio: 317/317;
	overflow: hidden;
	border-radius: 3rem;
	clip-path: content-box;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition: transform 0.3s;
	}
`;

const CardContentContainer = styled.div`
	display: flex;
	flex-direction: column;
	margin: 0 3rem;
`;

const CardContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		gap: 1.5rem;
	}
`;

const CardContentTop = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	margin-top: 2rem;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		gap: 0.5rem;
	}
`;

const CardContentBottom = styled.div`
	display: flex;
	flex-direction: column;
`;

const CardTitle = styled.h5`
	font-size: 2rem;
	font-weight: ${Typography.fontWeight.semiBold};
	line-height: 2.4rem;
	display: block;
	color: ${Colors.base.black};
`;

const CardTitleLink = styled.a`
	font-size: 2rem;
	font-weight: ${Typography.fontWeight.semiBold};
	line-height: 2.4rem;
	display: block;
	color: ${Colors.base.black};

	&::after {
		content: "";
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
	}
`;

const CardSubTitle = styled.p`
	font-size: 1.5rem;
	font-weight: ${Typography.fontWeight.semiBold};
	line-height: 1.9rem;
	display: block;
	color: ${Colors.base.black};
`;

const CardMeta = styled.p`
	font-size: 1.4rem;
	font-weight: ${Typography.fontWeight.medium};
	line-height: 1.8rem;
`;

const Button = styled.button`
	display: block;
	color: white;
	background-color: ${Colors.fp.pineGreen};
	height: clamp(4rem, 2.5vw, 5.3rem);
	width: clamp(4rem, 2.5vw, 5.3rem);
	border-radius: 50%;
	border: none;
	font-size: 20px;
	padding: 0;
	align-self: flex-end;
	margin: 0 1rem 1rem;
	cursor: pointer;

	svg {
		height: clamp(4rem, 2.5vw, 5.3rem);
		transition: transform 0.3s;
		position: relative;
	}

	&::after {
		content: none;
	}

	&:hover,
	&:focus {
		padding-right: 0;

		&::after {
			content: none;
		}
	}
`;
